"use client"

import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from "react"
import { useAuth } from "./AuthContext"

interface SchoolAccessContextType {
  hasAccess: boolean
  isLoading: boolean
  error: string | null
}

const SchoolAccessContext = createContext<SchoolAccessContextType>({
  hasAccess: false,
  isLoading: true,
  error: null,
})

export function SchoolAccessProvider({
  children,
  schoolId,
  schoolName,
}: {
  children: ReactNode
  schoolId: string
  schoolName: string
}) {
  const { user } = useAuth()
  const [hasAccess, setHasAccess] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  // Use a ref to track if we've already checked access
  const hasCheckedRef = useRef(false)
  // Store the request ID to avoid race conditions
  const requestIdRef = useRef(0)

  useEffect(() => {
    // Reset the check flag when dependencies change
    hasCheckedRef.current = false

    const checkAccess = async () => {
      // Skip if we've already checked
      if (hasCheckedRef.current) return

      // Mark that we've checked
      hasCheckedRef.current = true

      if (!user) {
        setHasAccess(false)
        setIsLoading(false)
        return
      }

      // Create a unique ID for this request
      const currentRequestId = ++requestIdRef.current

      try {
        const url = `/api/check-school-access?user_id=${user.uid}&school_id=${schoolId}`
        const response = await fetch(url)

        // If this is not the latest request, ignore the result
        if (currentRequestId !== requestIdRef.current) return

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`)
        }

        const data = await response.json()
        setHasAccess(data.hasAccess)

      } catch (error) {
        // Only update state if this is the latest request
        if (currentRequestId === requestIdRef.current) {
          console.error("Error checking access:", error)
          setError(error instanceof Error ? error.message : "Unknown error")
          setHasAccess(false)
        }
      } finally {
        // Only update loading state if this is the latest request
        if (currentRequestId === requestIdRef.current) {
          setIsLoading(false)
        }
      }
    }

    checkAccess()

    // Cleanup function to handle component unmounting
    return () => {
      // Increment the request ID to cancel any in-flight requests
      const currentRequestId = requestIdRef.current
      requestIdRef.current = currentRequestId + 1
    }
  }, [user, schoolId, schoolName])

  return <SchoolAccessContext.Provider value={{ hasAccess, isLoading, error }}>{children}</SchoolAccessContext.Provider>
}

export function useSchoolAccess() {
  return useContext(SchoolAccessContext)
}

